import { useEffect, useState } from "react";
import { Check, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useExtras } from "@/hooks/use-extras";
import { Burger } from "@/data/burgers";

interface AddOnsDialogProps {
  burger: Burger | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (burger: Burger, extras: string[]) => void;
}

export const AddOnsDialog = ({ burger, open, onOpenChange, onConfirm }: AddOnsDialogProps) => {
  const { extras } = useExtras();
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (open) setSelected([]);
  }, [open, burger?.id]);

  if (!burger) return null;

  const toggle = (name: string) =>
    setSelected((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const extrasTotal = selected.reduce(
    (sum, name) => sum + (extras.find((e) => e.name === name)?.price ?? 0),
    0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add-ons for {burger.name}</DialogTitle>
          <DialogDescription>Pick any extras you want before adding this burger to your cart.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[45vh] overflow-y-auto">
          {extras.length === 0 && (
            <p className="text-sm text-muted-foreground">No add-ons available right now.</p>
          )}
          {extras.map((extra) => {
            const active = selected.includes(extra.name);
            return (
              <button
                key={extra.id}
                type="button"
                onClick={() => toggle(extra.name)}
                aria-pressed={active}
                className={`w-full min-h-[44px] flex items-center justify-between gap-3 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "border-accent bg-accent/10 text-foreground"
                    : "border-border bg-card text-foreground hover:bg-muted"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`w-5 h-5 rounded-md border flex items-center justify-center ${
                      active ? "bg-accent border-accent" : "border-border"
                    }`}
                  >
                    {active && <Check className="w-3.5 h-3.5 text-accent-foreground" />}
                  </span>
                  {extra.name}
                </span>
                <span className="text-accent font-bold">+${extra.price.toFixed(2)}</span>
              </button>
            );
          })}
        </div>

        <DialogFooter className="sm:justify-between gap-2">
          <span className="text-sm text-muted-foreground self-center">
            Total: <span className="text-accent font-bold">${(burger.price + extrasTotal).toFixed(2)}</span>
          </span>
          <Button
            className="gradient-accent text-accent-foreground font-bold"
            onClick={() => {
              onConfirm(burger, selected);
              onOpenChange(false);
            }}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Add to Cart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
